import { cloneDeep, set } from 'lodash';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import { Session } from '@modules/husleie-framework/src/types/Session.entity';
import { SignerTypeEnum } from '@modules/husleie-framework/src/types/Signing.entity';
import RentalObjectConversionOperations from '@modules/store/rentalObject/static-classes/RentalObjectConversionOperations';
import SessionConversionOperations from '@modules/store/session/static-classes/SessionConversionOperations';
import type {
  SessionAPIInterface,
  SessionCreate,
  SessionCreateResponse,
  SessionClone,
  SessionCloneResponse,
  SessionGet,
  SessionGetResponse,
  SessionPreview,
  SessionPreviewResponse,
  SessionUpdate,
  SessionUpdateResponse,
  SessionDelete,
  SessionSigningMethodsGet,
  SessionSigningMethodsGetResponse,
  SessionSigningPrepare,
  SessionSigningPrepareResponse,
} from '@modules/store/session/types/SessionAPI.interface';
import { SessionStoreApi, SigningApi } from '@modules/smartmieteapi';

const type = 'inspection_report';

export default class API implements SessionAPIInterface {
  getSession(get: SessionGet): Promise<SessionGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new SessionStoreApi();
      api
        .getSession(type, get.uuid)
        .then((data: any) => {
          SessionConversionOperations.fileDetails = data.fileDetails;
          RentalObjectConversionOperations.fileDetails = data.fileDetails;
          const inspectionReport: Session = SessionConversionOperations.apiToInternal(
            get.type,
            {
              ...data.object,
              uuid: data.id,
              tenancyId: data.tenancyId,
              signingUuid: data.signingId,
              status: data.status,
              created: data.created,
              modified: data.modified,
            },
            get.userTranslationOptions
          );

          resolve({ session: inspectionReport });
        })
        .catch((error: Error) => {
          LogOperations.error('API|getSession', error);
          reject(error);
        });
    });
  }

  createSession(create: SessionCreate): Promise<SessionCreateResponse> {
    return new Promise((resolve, reject) => {
      const api = new SessionStoreApi();
      const body: any = {
        ...(create.sessionId && { session_id: create.sessionId }),
        ...(create.rentalObjectId && { rental_object_id: create.rentalObjectId }),
        ...(create.landlordId && { landlord_id: create.landlordId }),
        ...(create.tenancyId && { tenancy_id: create.tenancyId }),
      };

      api
        .createSession(type, { body })
        .then(async (data: any) => {
          if (!create.startDate) return data;

          const dataWithDates = cloneDeep(data.object);
          set(dataWithDates, 'rentalObject.details.moveInDate', create.startDate);

          if (create.endDate) {
            set(dataWithDates, 'rentalObject.details.moveOutDate', create.endDate);
          }

          await api.updateSession(type, data.id, {
            body: dataWithDates,
          });

          return {
            ...data,
            object: dataWithDates,
          };
        })
        .then((data: any) => {
          SessionConversionOperations.fileDetails = data.fileDetails;
          RentalObjectConversionOperations.fileDetails = data.fileDetails;
          const inspectionReport: Session = SessionConversionOperations.apiToInternal(
            create.type,
            {
              ...data.object,
              uuid: data.id,
              tenancyId: data.tenancyId,
              status: data.status,
              created: data.created,
              modified: data.modified,
            },
            create.userTranslationOptions
          );
          resolve({ session: inspectionReport });
        })
        .catch((error: Error) => {
          LogOperations.error('API|createSession', error);
          reject(error);
        });
    });
  }

  cloneSession(clone: SessionClone): Promise<SessionCloneResponse> {
    return new Promise((resolve, reject) => {
      const api = new SessionStoreApi();
      const body: any = {
        session_uuid: clone.uuid,
      };
      api
        // @ts-ignore
        .cloneSession({ body })
        .then((data: any) => {
          SessionConversionOperations.fileDetails = data.fileDetails;
          RentalObjectConversionOperations.fileDetails = data.fileDetails;
          const inspectionReport: Session = SessionConversionOperations.apiToInternal(
            clone.type,
            {
              ...data.object,
              uuid: data.id,
              tenancyId: data.tenancyId,
              status: data.status,
              created: data.created,
              modified: data.modified,
            },
            clone.userTranslationOptions
          );
          resolve({ session: inspectionReport });
        })
        .catch((error: Error) => {
          LogOperations.error('API|cloneSession', error);
          reject(error);
        });
    });
  }

  updateSession(update: SessionUpdate): Promise<SessionUpdateResponse> {
    return new Promise((resolve, reject) => {
      const api = new SessionStoreApi();
      const body: any = SessionConversionOperations.internalToApi(update.session);
      api
        .updateSession(type, update.session.uuid, { body })
        .then((data: any) => {
          const inspectionReport: Session = {
            ...update.session, // feed local version back io updated data
            status: data.status,
            created: data.created,
            modified: data.modified,
          };
          resolve({ session: inspectionReport });
        })
        .catch((error: Error) => {
          LogOperations.error('API|updateSession', error);
          reject(error);
        });
    });
  }

  deleteSession(del: SessionDelete): Promise<void> {
    return new Promise((resolve, reject) => {
      const api = new SessionStoreApi();
      api.deleteSession(type, del.uuid).catch((error: Error) => {
        LogOperations.error('API|deleteSession', error);
        reject(error);
      });
    });
  }

  fetchSessionPreview(download: SessionPreview): Promise<SessionPreviewResponse> {
    return new Promise((resolve, reject) => {
      const api = new SessionStoreApi();
      const query: any = { format: download.format };

      api
        .previewSession(type, download.uuid, { query }, download.responseType)
        .then((data: any) => {
          switch (download.format) {
            case 'html':
              resolve({ html: data });
              break;
            case 'pdf':
              resolve({ pdf: data });
              break;
          }
        })
        .catch((error: Error) => {
          LogOperations.error('API|previewSession', error);
          reject(error);
        });
    });
  }

  getSessionSigningMethods(get: SessionSigningMethodsGet): Promise<SessionSigningMethodsGetResponse> {
    const api = new SessionStoreApi();
    return api.getSigningMethods(type, get.uuid);
  }

  async prepareSessionSigning(prepare: SessionSigningPrepare): Promise<SessionSigningPrepareResponse> {
    const sessionStoreApi = new SessionStoreApi();

    const prepareBody = { signingMethod: prepare.method };
    const signingBody = await sessionStoreApi.prepareSigning(type, prepare.uuid, { body: prepareBody });

    const signingApi = new SigningApi();

    const signingData: any = await signingApi.createSigning({ body: signingBody });
    const currentSigners: any[] = signingData?.nextSigners ? signingData.nextSigners : [];
    const landlordSigner = currentSigners.find((landlord: any) => landlord.type === SignerTypeEnum.LANDLORD);
    const landlordSignerUuid: undefined | string = landlordSigner?.id;
    const landlordSignerStatus: undefined | string = landlordSigner?.status;

    if (!landlordSignerUuid || !landlordSignerStatus || landlordSignerStatus !== 'pending') {
      throw new Error('No landlord signer found or invalid signer signing status');
    }

    const data: SessionSigningPrepareResponse = {
      uuid: signingData.id,
      landlordSignerUuid,
    };

    return Promise.resolve(data);
  }
}
